import React, { useState, useMemo, useEffect } from 'react';
import { Megaphone, RefreshCcw, DollarSign, Target, CheckCircle, Clock, XCircle, Users, Trash2 } from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import { QawaemTableStickyActionsTd } from '../../products/saudi/components/QawaemTableStickyActions';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { useTranslation } from 'react-i18next';
import { useCrmPage, useCrmData, calcCampaignRoi, resolveCrmModalTabs, useCrmEntityReference } from '../../shared/utils/crmPageUtils';
import { enrichStatusOptions } from '../../shared/utils/crmPageUtils';
import { getMarketingChannelLabel } from '../../shared/utils/brandRegions';
import { CRM_CAMPAIGN_TYPES } from '../../shared/utils/crmConstants';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import QawaemErpListPageShell, { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';
import QawaemErpCrudModal from '../../products/saudi/components/QawaemErpCrudModal';
import {
  CrmModalFormTabs,
  CrmModalFormBody,
  CrmModalFormPanel,
  CrmModalFieldGrid,
} from './components/CrmModalFormTabs';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import CrmEntityReferenceField from './components/CrmEntityReferenceField';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { useSettings } from '../../core/contexts/SettingsContext';
import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import { useCrmConfig } from './useCrmConfig';
import { isCrmAutoReferenceEnabled, resolveCrmListViewMode } from './crmConfigDefaults';
import { filterCrmRecordsByDateRange, sliceCrmListItems } from './crmListPageUtils';

const CAMPAIGN_TABLE_COLUMNS = [
  { key: 'ref', labelKey: 'col_ref' },
  { key: 'name', labelKey: 'col_name' },
  { key: 'type', labelKey: 'col_type' },
  { key: 'channel', labelKey: 'col_channel' },
  { key: 'budget', labelKey: 'col_budget' },
  { key: 'spent', labelKey: 'col_spent' },
  { key: 'roi', labelKey: 'col_roi' },
  { key: 'dates', labelKey: 'col_dates' },
  { key: 'status', labelKey: 'col_status', align: 'center' },
];

const CAMPAIGN_STATUS = [
  { value: 'draft', labelKey: 'status_draft' },
  { value: 'active', labelKey: 'status_active' },
  { value: 'paused', labelKey: 'status_paused' },
  { value: 'completed', labelKey: 'status_completed' },
];

const emptyForm = () => ({
  campaign_ref: '',
  name: '', type: 'whatsapp', status: 'draft', budget: '', spent: '',
  start_date: '', end_date: '', target_audience: '', description: '',
  leads_generated: '', conversions: '',
});

const FORM_ID = 'crm-campaigns-form';
const CAMPAIGN_MODAL_FIELD_COUNT = 12;

const CampaignsPage = () => {
  const { t } = useTranslation();
  const cfgTheme = useModuleConfigTheme();
  const crmConfig = useCrmConfig();
  const { settings } = useSettings();
  const { dateRange } = useSaudiListDateRange();
  const { p, fmtDate, fmtMoney } = useCrmPage('campaignsPage');
  const ps = (key, opts) => t(`modules.crm.pages.shared.${key}`, opts);
  const { items: campaigns, loading, load, save, remove } = useCrmData('/crm/campaigns');
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: p('title') });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: entityLabel });
  const typeOptions = useMemo(() => CRM_CAMPAIGN_TYPES.map((item) => ({ value: item.value, label: item.label })), []);
  const channelLabel = (type) => {
    const chLabel = getMarketingChannelLabel(type);
    if (chLabel && chLabel !== type) return chLabel;
    return typeOptions.find((item) => item.value === type)?.label || type || '—';
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table');
  useEffect(() => {
    setViewMode(resolveCrmListViewMode(crmConfig.defaultView));
  }, [crmConfig.defaultView]);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [formTab, setFormTab] = useState('general');
  const [columnVisibility, setColumnVisibility] = useState(() =>
    Object.fromEntries(CAMPAIGN_TABLE_COLUMNS.map((c) => [c.key, true]))
  );

  const generateRef = useCrmEntityReference('campaign', 'CMP', campaigns, 'campaign_ref');

  const qawaemColumnDefs = useMemo(
    () => CAMPAIGN_TABLE_COLUMNS.map((col) => ({
      key: col.key,
      label: p(col.labelKey),
      checked: columnVisibility[col.key] !== false,
    })),
    [columnVisibility, p],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const visibleColumns = useMemo(
    () => CAMPAIGN_TABLE_COLUMNS.filter((c) => columnVisibility[c.key] !== false),
    [columnVisibility],
  );
  const tableColSpan = visibleColumns.length + 2;

  const statusOptions = useMemo(() => enrichStatusOptions(CAMPAIGN_STATUS.map((s) => ({
    value: s.value,
    label: p(s.labelKey),
    icon: s.value === 'active' ? CheckCircle : s.value === 'paused' ? Clock : s.value === 'completed' ? Target : Megaphone,
  }))), [p]);

  const statusLabel = (s) => p(CAMPAIGN_STATUS.find((x) => x.value === s)?.labelKey || 'status_draft');

  const getCampaignFilterValue = (c, colKey) => {
    switch (colKey) {
      case 'ref': return c.campaign_ref || '';
      case 'name': return c.name || '';
      case 'type': return typeOptions.find((item) => item.value === c.type)?.label || c.type || '';
      case 'channel': {
        const chLabel = getMarketingChannelLabel(c.type);
        if (chLabel && chLabel !== c.type) return chLabel;
        return typeOptions.find((item) => item.value === c.type)?.label || c.type || '';
      }
      case 'budget': return fmtMoney(c.budget);
      case 'spent': return fmtMoney(c.spent);
      case 'roi': return `${calcCampaignRoi(c.spent, c.conversions, c.budget)}%`;
      case 'dates': return `${fmtDate(c.start_date)} — ${fmtDate(c.end_date)}`;
      case 'status': return statusLabel(c.status);
      default: return '';
    }
  };

  const columnFilterRules = useMemo(
    () => visibleColumns.map((col) => {
      const getValue = (row) => getCampaignFilterValue(row, col.key);
      if (col.key === 'status' || col.key === 'type') return exactColumnRule(col.key, getValue);
      return textColumnRule(col.key, getValue);
    }),
    [visibleColumns, typeOptions, fmtDate, fmtMoney],
  );

  const dateFiltered = useMemo(
    () => filterCrmRecordsByDateRange(campaigns, dateRange, ['start_date', 'created_at', 'updated_at']),
    [campaigns, dateRange],
  );

  const searchFiltered = useMemo(() => {
    if (viewMode !== 'cards' || !searchTerm) return dateFiltered;
    const q = searchTerm.toLowerCase();
    return dateFiltered.filter((c) =>
      c.name?.toLowerCase().includes(q) || c.campaign_ref?.toLowerCase().includes(q)
    );
  }, [dateFiltered, viewMode, searchTerm]);

  const filtered = useMemo(
    () => applyColumnFilters(searchFiltered, colFilters, columnFilterRules),
    [searchFiltered, colFilters, columnFilterRules],
  );

  const pagedFiltered = useMemo(
    () => sliceCrmListItems(filtered, crmConfig.itemsPerPage),
    [filtered, crmConfig.itemsPerPage],
  );

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filtered);

  useEffect(() => {
    if (selectedRecords.length > 0) setEditingKey(null);
  }, [selectedRecords, setEditingKey]);

  const handleBulkDelete = async () => {
    if (!selectedRecords.length || !window.confirm(p('delete_confirm'))) return;
    try {
      const deleteCount = selectedRecords.length;
      await Promise.all(selectedRecords.map((id) => remove(id)));
      notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
      setSelectedRecords([]);
    } catch {
      notifyError(showNotify, p('save_error'));
    }
  };

  const statusFilterOptions = useMemo(
    () => statusOptions.map((s) => ({ value: s.label, label: s.label })),
    [statusOptions],
  );

  const typeFilterOptions = useMemo(
    () => typeOptions.map((s) => ({ value: s.label, label: s.label })),
    [typeOptions],
  );

  const stats = useMemo(() => {
    const totalBudget = searchFiltered.reduce((s, c) => s + (parseFloat(c.budget) || 0), 0);
    const totalSpent = searchFiltered.reduce((s, c) => s + (parseFloat(c.spent) || 0), 0);
    const active = searchFiltered.filter((c) => c.status === 'active').length;
    const avgRoi = searchFiltered.length
      ? searchFiltered.reduce((s, c) => s + parseFloat(calcCampaignRoi(c.spent, c.conversions, c.budget)), 0) / searchFiltered.length
      : 0;
    return { total: searchFiltered.length, active, totalBudget, totalSpent, avgRoi: avgRoi.toFixed(1) };
  }, [searchFiltered]);

  const statCards = useMemo(() => sliceQawaemStats([
    { key: 'total', label: p('stat_total'), value: stats.total, icon: Megaphone, variant: 'copper' },
    { key: 'active', label: p('stat_active'), value: stats.active, icon: CheckCircle, variant: 'forest' },
    { key: 'budget', label: p('stat_budget'), value: fmtMoney(stats.totalBudget), icon: DollarSign, variant: 'copper' },
    { key: 'spent', label: p('stat_spent'), value: fmtMoney(stats.totalSpent), icon: Target, variant: 'forest' },
  ], 4), [stats, p, fmtMoney]);

  const handleStatusChange = async (recordId, newStatus) => {
    const campaign = campaigns.find((c) => c.id === recordId);
    if (!campaign) return;
    try {
      await save({ ...campaign, status: newStatus }, recordId);
    } catch {
      alert(p('save_error'));
    }
  };

  const campaignFormTabs = useMemo(() => resolveCrmModalTabs(CAMPAIGN_MODAL_FIELD_COUNT, [
    { id: 'general', label: p('tab_general', { defaultValue: 'عام' }), icon: Megaphone },
    { id: 'details', label: p('tab_details', { defaultValue: 'التفاصيل' }), icon: Users, iconClassName: 'text-blue-400' },
  ]), [p]);
  const campaignDefaultTab = campaignFormTabs[0]?.id || 'general';

  const closeModal = () => {
    setFormTab(campaignDefaultTab);
    setShowModal(false);
  };

  const openNew = () => {
    setEditing(null);
    const next = { ...emptyForm() };
    if (isCrmAutoReferenceEnabled(settings, 'campaign')) {
      next.campaign_ref = generateRef();
    }
    setForm(next);
    setFormTab(campaignDefaultTab);
    setShowModal(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ ...emptyForm(), ...r, campaign_ref: r.campaign_ref || '' });
    setFormTab(campaignDefaultTab);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(p('delete_confirm'))) return;
    try {
      await remove(id);
      notifyDeleted(showNotify, entityLabel);
    } catch {
      notifyError(showNotify, p('save_error'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        campaign_ref: (form.campaign_ref || '').trim() || (isCrmAutoReferenceEnabled(settings, 'campaign') ? generateRef() : ''),
      };
      await save(payload, editing?.id);
      if (editing?.id) {
        notifyUpdated(showNotify, entityLabel);
      } else {
        notifyCreated(showNotify, entityLabel);
      }
      closeModal();
    } catch {
      notifyError(showNotify, p('save_error'));
    }
  };

  const renderCampaignCell = (c, col) => {
    switch (col.key) {
      case 'ref':
        return <td key={col.key} className="px-4 py-3 text-white/80 text-sm font-mono text-xs">{c.campaign_ref || '—'}</td>;
      case 'name':
        return <td key={col.key} className="px-4 py-3 text-white font-medium">{c.name}</td>;
      case 'type':
        return <td key={col.key} className="px-4 py-3 text-white/70 text-sm">{typeOptions.find((item) => item.value === c.type)?.label || c.type}</td>;
      case 'channel':
        return <td key={col.key} className="px-4 py-3 text-white/70 text-sm">{channelLabel(c.type)}</td>;
      case 'budget':
        return <td key={col.key} className="px-4 py-3 text-white font-semibold">{fmtMoney(c.budget)}</td>;
      case 'spent':
        return <td key={col.key} className="px-4 py-3 text-white/80">{fmtMoney(c.spent)}</td>;
      case 'roi':
        return <td key={col.key} className="px-4 py-3 text-emerald-400">{calcCampaignRoi(c.spent, c.conversions, c.budget)}%</td>;
      case 'dates':
        return <td key={col.key} className="px-4 py-3 text-white/60 text-xs">{fmtDate(c.start_date)} — {fmtDate(c.end_date)}</td>;
      case 'status':
        return (
          <td key={col.key} className="px-4 py-3 text-center">
            <div className="flex justify-center">
              <QawaemClickableStatusBadge
                status={c.status}
                recordId={c.id}
                options={statusOptions}
                onChange={handleStatusChange}
                title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
              />
            </div>
          </td>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <QawaemErpListPageShell
        title={p('title')}
        subtitle={p('subtitle')}
        icon={Megaphone}
        stats={statCards}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={selectedRecords.length > 0 ? (
          <button
            type="button"
            onClick={handleBulkDelete}
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
          >
            <Trash2 size={16} />
            <span>{t('common.delete')} ({selectedRecords.length})</span>
          </button>
        ) : null}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: 'بحث…' })}
        onCreate={openNew}
        createLabel={p('add')}
        columns={qawaemColumnDefs}
        visibleColumnCount={qawaemVisibleColumnCount}
        totalColumnCount={qawaemColumnDefs.length}
        headerActions={(
          <button type="button" onClick={load} className="refresh-button" aria-label={p('refresh', { defaultValue: 'تحديث' })}>
            <RefreshCcw size={20} className={loading ? 'animate-spin' : ''} />
          </button>
        )}
      >
        {viewMode === 'table' ? (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {visibleColumns.map((col) => (
                    <TableColumnHeaderFilter
                      key={col.key}
                      columnKey={col.key}
                      label={p(col.labelKey)}
                      kind={col.key === 'status' || col.key === 'type' ? 'select' : 'text'}
                      value={colFilters[col.key] ?? ''}
                      onChange={(v) => setColFilter(col.key, v)}
                      options={col.key === 'status' ? statusFilterOptions : col.key === 'type' ? typeFilterOptions : undefined}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      align={col.align === 'center' ? 'center' : undefined}
                      thClassName="text-white font-semibold text-sm"
                      className="px-4 py-3"
                    />
                  ))}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName="px-4 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {loading && !campaigns.length ? (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60">{ps('loading')}</td></tr>
                ) : filtered.length ? pagedFiltered.map((c) => (
                  <tr key={c.id} className={`hover:bg-white/5 ${selectedRecords.includes(c.id) ? 'bg-white/10' : ''}`}>
                    <QawaemTableSelectRowCell
                      checked={selectedRecords.includes(c.id)}
                      onChange={() => handleSelectRecord(c.id)}
                    />
                    {visibleColumns.map((col) => renderCampaignCell(c, col))}
                    <QawaemTableStickyActionsTd>
                      <QawaemErpRowActions
                        onEdit={() => openEdit(c)}
                        onDelete={() => handleDelete(c.id)}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </QawaemTableStickyActionsTd>
                  </tr>
                )) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60">{p('empty')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-white/60">{p('empty')}</div>
        ) : (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4">
            {pagedFiltered.map((c) => (
              <div key={c.id} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 h-full min-w-0">
                <div className="flex justify-between items-start mb-3">
                  <p className="text-white font-semibold">{c.name}</p>
                  <QawaemClickableStatusBadge
                    status={c.status}
                    recordId={c.id}
                    options={statusOptions}
                    onChange={handleStatusChange}
                  />
                </div>
                <p className="text-white/50 text-xs mb-2">{c.campaign_ref || '—'}</p>
                <p className="text-white/60 text-sm">{typeOptions.find((item) => item.value === c.type)?.label || c.type}</p>
                <p className="text-white font-bold mt-2">{fmtMoney(c.budget)}</p>
                <p className="text-emerald-400 text-sm">{calcCampaignRoi(c.spent, c.conversions, c.budget)}% ROI</p>
                <QawaemErpRowActions
                  compact
                  size={14}
                  className="mt-3 justify-start"
                  onEdit={() => openEdit(c)}
                  onDelete={() => handleDelete(c.id)}
                  editTitle={t('common.edit')}
                  deleteTitle={t('common.delete')}
                />
              </div>
            ))}
          </div>
        )}
      </QawaemErpListPageShell>

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={closeModal}
          title={editing ? p('modal_edit') : p('modal_new')}
          icon={Megaphone}
          formId={FORM_ID}
          saveLabel={t('common.save')}
          onSubmit
          maxWidth="1200px"
          testId="crm-campaigns-modal"
        >
          {campaignFormTabs.length > 0 ? (
            <CrmModalFormBody>
              <CrmModalFormTabs
                tabs={campaignFormTabs}
                activeTab={formTab}
                onTabChange={setFormTab}
                ariaLabel={editing ? p('modal_edit') : p('modal_new')}
              />
              <CrmModalFormPanel>
                <form id={FORM_ID} onSubmit={handleSubmit}>
                  {formTab === 'general' && (
                    <CrmModalFieldGrid>
                      <CrmEntityReferenceField
                        value={form.campaign_ref}
                        onChange={(v) => setForm({ ...form, campaign_ref: v })}
                        onRegenerate={editing ? undefined : () => setForm({ ...form, campaign_ref: generateRef() })}
                        label={p('field_ref', { defaultValue: ps('field_code_required') })}
                        placeholder={p('placeholder_ref', { defaultValue: ps('placeholder_code') })}
                        autoGeneratedHint={editing ? undefined : ps('auto_generated')}
                        regenerateTitle={ps('regenerate_code')}
                        labelClassName={cfgTheme.label}
                        inputClassName={cfgTheme.input}
                      />
                      <div>
                        <label className={cfgTheme.label}>{p('field_name')}<span className="text-red-600 font-bold"> *</span></label>
                        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={cfgTheme.input} />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('field_type')}</label>
                        <CustomSelect value={form.type} onChange={(v) => setForm({ ...form, type: v })} options={typeOptions} />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('col_status')}</label>
                        <CustomSelect
                          value={form.status}
                          onChange={(v) => setForm({ ...form, status: v })}
                          options={statusOptions.map((s) => ({ value: s.value, label: s.label }))}
                        />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('field_budget')}<span className="text-red-600 font-bold"> *</span></label>
                        <input type="number" step="0.01" required value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className={cfgTheme.input} />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('field_spent')}</label>
                        <input type="number" step="0.01" value={form.spent} onChange={(e) => setForm({ ...form, spent: e.target.value })} className={cfgTheme.input} />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('field_start')}</label>
                        <CustomDatePicker value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} showTime={false} />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('field_end')}</label>
                        <CustomDatePicker value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} showTime={false} />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('field_leads', { defaultValue: 'العملاء المحتملون' })}</label>
                        <input type="number" value={form.leads_generated || ''} onChange={(e) => setForm({ ...form, leads_generated: e.target.value })} className={cfgTheme.input} />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('field_conversions', { defaultValue: 'التحويلات' })}</label>
                        <input type="number" value={form.conversions || ''} onChange={(e) => setForm({ ...form, conversions: e.target.value })} className={cfgTheme.input} />
                      </div>
                    </CrmModalFieldGrid>
                  )}
                  {formTab === 'details' && (
                    <CrmModalFieldGrid>
                      <div>
                        <label className={cfgTheme.label}>{p('field_audience')}</label>
                        <input value={form.target_audience || ''} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} className={cfgTheme.input} />
                      </div>
                      <div className="md:col-span-3">
                        <label className={cfgTheme.label}>{p('field_description', { defaultValue: 'الوصف' })}</label>
                        <textarea value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className={`${cfgTheme.input} resize-none`} />
                      </div>
                    </CrmModalFieldGrid>
                  )}
                </form>
              </CrmModalFormPanel>
            </CrmModalFormBody>
          ) : (
            <form id={FORM_ID} onSubmit={handleSubmit}>
              <CrmModalFieldGrid>
                <CrmEntityReferenceField
                  value={form.campaign_ref}
                  onChange={(v) => setForm({ ...form, campaign_ref: v })}
                  onRegenerate={editing ? undefined : () => setForm({ ...form, campaign_ref: generateRef() })}
                  label={p('field_ref', { defaultValue: ps('field_code_required') })}
                  placeholder={p('placeholder_ref', { defaultValue: ps('placeholder_code') })}
                  autoGeneratedHint={editing ? undefined : ps('auto_generated')}
                  regenerateTitle={ps('regenerate_code')}
                  labelClassName={cfgTheme.label}
                  inputClassName={cfgTheme.input}
                />
                <div>
                  <label className={cfgTheme.label}>{p('field_name')}<span className="text-red-600 font-bold"> *</span></label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={cfgTheme.input} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('field_type')}</label>
                  <CustomSelect value={form.type} onChange={(v) => setForm({ ...form, type: v })} options={typeOptions} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('col_status')}</label>
                  <CustomSelect
                    value={form.status}
                    onChange={(v) => setForm({ ...form, status: v })}
                    options={statusOptions.map((s) => ({ value: s.value, label: s.label }))}
                  />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('field_budget')}</label>
                  <input type="number" step="0.01" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className={cfgTheme.input} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('field_spent')}</label>
                  <input type="number" step="0.01" value={form.spent} onChange={(e) => setForm({ ...form, spent: e.target.value })} className={cfgTheme.input} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('field_start')}</label>
                  <CustomDatePicker value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} showTime={false} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('field_end')}</label>
                  <CustomDatePicker value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} showTime={false} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('field_leads', { defaultValue: 'العملاء المحتملون' })}</label>
                  <input type="number" value={form.leads_generated || ''} onChange={(e) => setForm({ ...form, leads_generated: e.target.value })} className={cfgTheme.input} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('field_conversions', { defaultValue: 'التحويلات' })}</label>
                  <input type="number" value={form.conversions || ''} onChange={(e) => setForm({ ...form, conversions: e.target.value })} className={cfgTheme.input} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('field_audience')}</label>
                  <input value={form.target_audience || ''} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} className={cfgTheme.input} />
                </div>
                <div className="md:col-span-3">
                  <label className={cfgTheme.label}>{p('field_description', { defaultValue: 'الوصف' })}</label>
                  <textarea value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className={`${cfgTheme.input} resize-none`} />
                </div>
              </CrmModalFieldGrid>
            </form>
          )}
        </QawaemErpCrudModal>
      )}

      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default CampaignsPage;
